import { useFirebase } from '@chem-po/firebase-web'

import { useCollection, UseConsentFormsStatus } from '@hb/react'
import {
  CONSENT_FORMS,
  ConsentForm,
  midwivesCollection,
  objectToArray,
  PatientConsentFormStatus,
  PopulatedAssessment,
  PracticeWithAdmin,
  User,
  USERS,
  WithId,
} from '@hb/shared'
import { collection, query, Query } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '../backend/useQuery'
import { useMissingConsentForms } from './useMissingConsentForms'
import { useSignConsentForm } from './useSignConsentForm'

export const useConsentFormsStatus = (
  userId: string | null,
  user: User | null,
  assessment: PopulatedAssessment | null,
): UseConsentFormsStatus => {
  const { firestore } = useFirebase()
  const [selectedConsentFormId, setSelectedConsentFormId] = useState<string | null>(null)

  const { consentFormsStatus: status } = user ?? {}
  const { itemsMap: practices } = useCollection(midwivesCollection)

  const consentFormsQuery = useMemo(
    () =>
      userId
        ? (query(collection(firestore, USERS, userId, CONSENT_FORMS)) as Query<ConsentForm>)
        : null,
    [userId, firestore],
  )
  const { data: consentForms } = useQuery(consentFormsQuery)

  const handleViewConsentForm = useCallback((consentFormId: string) => {
    setSelectedConsentFormId(consentFormId)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedConsentFormId(null)
  }, [])

  const { hb: unsortedHbConsentForms, practice: unsortedPracticeConsentForms } = useMemo(() => {
    const statusArr = objectToArray(status ?? {})
    return statusArr.reduce<{
      hb: WithId<PatientConsentFormStatus>[]
      practice: {
        practiceId: string
        practice: PracticeWithAdmin | null
        forms: WithId<PatientConsentFormStatus>[]
      }[]
    }>(
      (acc, form) => {
        if (form.practiceId) {
          const practice = practices[form.practiceId] ?? null
          const existing = acc.practice.find(p => p.practiceId === form.practiceId)
          if (existing) {
            existing.forms.push(form)
          } else {
            acc.practice.push({
              practiceId: form.practiceId,
              practice,
              forms: [form],
            })
          }
        } else {
          acc.hb.push(form)
        }
        return acc
      },
      { hb: [], practice: [] },
    )
  }, [status, practices])

  const sentHbConsentForms = useMemo(
    () =>
      unsortedHbConsentForms.sort((a, b) => {
        if (a.practiceId && a.practiceId === b.practiceId) {
          return a.sentOn - b.sentOn
        }
        return a.practiceId?.localeCompare(b.practiceId ?? '') ?? 0
      }),
    [unsortedHbConsentForms],
  )
  const sentPracticeConsentForms = useMemo(
    () =>
      unsortedPracticeConsentForms
        .map(p => ({
          ...p,
          forms: p.forms.sort((a, b) => a.sentOn - b.sentOn),
        }))
        .sort((a, b) => a.practice?.name?.localeCompare(b.practice?.name ?? '') ?? 0),
    [unsortedPracticeConsentForms],
  )

  const numConsentForms = useMemo(
    () =>
      sentHbConsentForms.length +
      sentPracticeConsentForms.reduce((acc, p) => acc + p.forms.length, 0),
    [sentHbConsentForms, sentPracticeConsentForms],
  )

  const numSignedConsentForms = useMemo(
    () =>
      sentHbConsentForms.filter(form => !!form.signedOn).length +
      sentPracticeConsentForms.reduce(
        (acc, p) => acc + p.forms.filter(form => !!form.signedOn).length,
        0,
      ),
    [sentHbConsentForms, sentPracticeConsentForms],
  )

  const selectedConsentForm = useMemo<WithId<ConsentForm> | null>(() => {
    if (!selectedConsentFormId) return null
    const form = consentForms?.[selectedConsentFormId]
    if (!form) return null
    return {
      id: selectedConsentFormId,
      ...form,
    }
  }, [consentForms, selectedConsentFormId])

  const { onSignConsentForm, canDownload } = useSignConsentForm(selectedConsentForm)
  const assessmentConsentFormsComplete = useMemo(
    () => Object.values(status ?? {}).every(form => !!form.signedOn),
    [status],
  )

  const missingConsentForms = useMissingConsentForms(assessment, consentForms)

  return {
    hbConsentForms: sentHbConsentForms,
    practiceConsentForms: sentPracticeConsentForms,
    selectedConsentForm,
    consentForms: consentForms ?? null,
    missingConsentForms,
    handleViewConsentForm,
    handleCloseModal,
    onSignConsentForm,
    canDownload,
    complete: assessmentConsentFormsComplete,
    numConsentForms,
    numSignedConsentForms,
  }
}
